import { ApplicationWithId } from "../interfaces/IApplications";
import { ContestWithId } from "../interfaces/IContests";
import { FormData } from "../interfaces/IForms";
import { GroupWithId } from "../interfaces/IGroups";
import { ParticipantChanges, ParticipantWithId } from "../interfaces/IParticipants";
import { PresentationWithId } from "../interfaces/IPresentations";

export function compareParticipants(
  oldParticipants: ParticipantWithId[],
  newParticipants: ParticipantWithId[],
): { addedParticipants: string[]; removedParticipants: string[] } {
  const oldIds = new Set(oldParticipants.map((p) => p.id));
  const newIds = new Set(newParticipants.map((p) => p.id));

  const addedParticipants = newParticipants
    .filter((p) => !oldIds.has(p.id))
    .map((p) => p.id) as string[];
  const removedParticipants = oldParticipants
    .filter((p) => !newIds.has(p.id))
    .map((p) => p.id) as string[];

  return {
    addedParticipants,
    removedParticipants,
  };
}

export const filterParticipantsByDateRange = (
  participants: ParticipantWithId[],
  minDate: Date | null,
  maxDate: Date | null,
): ParticipantWithId[] => {
  return participants.filter((participant) => {
    const birthDate = new Date(participant.birthDate);
    const isAfterMinDate = minDate ? birthDate >= minDate : true;
    const isBeforeMaxDate = maxDate ? birthDate <= maxDate : true;
    return isAfterMinDate && isBeforeMaxDate;
  });
};

export function filterParticipantsInCategory(
  participants: ParticipantWithId[],
  presentations: PresentationWithId[],
): ParticipantWithId[] {
  // Zbierz wszystkich zawodników przypisanych do prezentacji w tej kategorii
  const assignedParticipantIds = new Set(
    presentations.flatMap((presentation) => presentation.participants.map((p) => p.id)),
  );
  // Filtruj zawodników, którzy nie są przypisani do żadnej prezentacji w tej kategorii
  return participants.filter((participant) => !assignedParticipantIds.has(participant.id));
}

export function formatAmount(value: number) {
  return (
    value.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " PLN"
  );
}

export function formatTimeInHoursAndMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${minutes}m (${hours}h ${remainingMinutes}m)`;
}

export function mapFormDataToObject<T>(data: FormData, baseObject: T): T {
  const mappedObject: T = { ...baseObject };
  (Object.keys(data) as Array<keyof T>).forEach((key) => {
    if (data.hasOwnProperty(key as string)) {
      mappedObject[key] = data[key as string] as T[keyof T];
    }
  });

  return mappedObject;
}

export function updateParticipantsMapInApplication(
  application: ApplicationWithId,
  participantChanges: ParticipantChanges,
): ApplicationWithId {
  const updatedParticipantPresentationsMap = { ...application.participantPresentationsMap };

  participantChanges.addedParticipants.forEach((participantId) => {
    if (participantId in updatedParticipantPresentationsMap) {
      updatedParticipantPresentationsMap[participantId]++;
    } else {
      updatedParticipantPresentationsMap[participantId] = 1;
    }
  });

  participantChanges.removedParticipants.forEach((participantId) => {
    if (participantId in updatedParticipantPresentationsMap) {
      const currentCount = updatedParticipantPresentationsMap[participantId];
      if (currentCount === 1) {
        delete updatedParticipantPresentationsMap[participantId];
      } else {
        updatedParticipantPresentationsMap[participantId]--;
      }
    }
  });

  return {
    ...application,
    participantPresentationsMap: updatedParticipantPresentationsMap,
  };
}

export function generateParticipantKey(
  firstName: string,
  lastName: string,
  birthDate: string,
): string {
  const normalize = (str: string) => str.toLowerCase().replace(/[^a-z0-9]/g, "");
  const datePart = birthDate.replace(/[^0-9]/g, "");
  return `${normalize(firstName)}${normalize(lastName)}${datePart}`;
}

// Funkcja do normalizacji imion i nazwisk
export const normalizeName = (name: string, allowMultipleWords: boolean = true): string => {
  if (!name) return name;

  if (allowMultipleWords) {
    // Dla nazwisk - obsługuje wieloczłonowe (np. "Jan-Kowalski", "Van Der Berg")
    return name
      .toLowerCase()
      .split(/[\s-]+/) // Dzieli po jednym lub więcej białych znakach lub myślniku
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("-"); // Łączymy z powrotem używając myślnika
  } else {
    // Dla imion - tylko pojedyncze słowo
    const cleanName = name.toLowerCase().trim();
    return cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
  }
};

// Funkcja do normalizacji imion (pojedyncze słowo) - alias dla kompatybilności
export const normalizeFirstName = (name: string): string => {
  return normalizeName(name, false);
};

/**
 * Sprawdza czy nazwa grupy już istnieje w podanej liście grup
 * @param groups - Lista grup z kontekstu (już załadowane)
 * @param name - Nazwa do sprawdzenia
 * @param excludeGroupId - ID grupy do wykluczenia z sprawdzenia (dla edycji)
 * @returns true jeśli nazwa już istnieje, false w przeciwnym razie
 */
export const checkGroupNameExists = (
  groups: GroupWithId[],
  name: string,
  excludeGroupId?: string,
): boolean => {
  const normalizedName = name.trim().toLowerCase();
  return groups.some(
    (group) => group.id !== excludeGroupId && group.name.trim().toLowerCase() === normalizedName,
  );
};

/**
 * Sprawdza czy rejestracja na turniej jest otwarta
 * @param contest - Obiekt turnieju
 * @returns true jeśli rejestracja jest otwarta, false w przeciwnym razie
 */
export const isRegistrationOpen = (contest: ContestWithId | undefined): boolean => {
  if (!contest) return false;
  // Jeżeli administrator ręcznie zakończył rejestrację – zawsze zamknięta
  if ((contest as any).registrationCompleted === true) return false;
  if (!contest.registrationEnd) return false;
  return new Date() <= new Date(contest.registrationEnd);
};
